import "../../scss/projects.scss";
import '@fortawesome/fontawesome-free/css/all.min.css';

import {Dom} from '../common/util/Dom';

import {addForm, setupAddableSelect, setupNormalSelect, setupSlimSelect} from '../common/manageForm';
import {setupExport} from './manageExport';
import {setupProjectTemplateManagement} from './templateManagement';

import {initTiptap} from '../common/setupTiptap';
import { initializeProjectsTable } from './projectsTable';
import { setupTextModals } from "./setupModal";
import { Datepicker } from 'vanillajs-datepicker';
import { setupPrivacyPopup } from '../common/util/util';
import Filesystem from './documentsFs/Filesystem.svelte';
import { setupConsultantLinks } from './consultant';
import { initUserMenu } from '../common/navRail';
import { initializeProjectsMap, updateMapFromTable } from './projectsMap';
import { AutoSaveManager } from '../common/util/AutoSaveManager.js';
import './kanbanDragDrop.js';
import './boardLayoutModal.js';
import { TagTextInput } from '../common/components/TagTextInput.js';
import { initArchiveAnalysis } from './archiveAnalysis.js';
import { installPartnerPicker } from '../common/partner_picker.js';
import { initContactRecommendRating } from './contactRecommendRating.js';
import { initFieldGuidelines } from './fieldGuidelines.js';
import { installContactModal } from './contactModal.js';

// Make Datepicker available globally for projectsTable
window.Datepicker = Datepicker;

/**
 * Initialize the collapsible filters sidebar functionality
 */
function initializeFiltersCollapse() {
    const collapseBtn = document.getElementById('filtersCollapseBtn');
    const filtersSidebar = document.getElementById('filtersSidebar');

    if (!collapseBtn || !filtersSidebar) return;

    const isMobile = () => window.innerWidth < 768;

    if (isMobile()) {
        // Mobile: start collapsed, use mobile-collapsed class
        filtersSidebar.classList.add('mobile-collapsed');
    } else {
        // Desktop: restore persisted collapsed state
        const isCollapsed = localStorage.getItem('projects_filters_collapsed') === 'true';
        if (isCollapsed) {
            filtersSidebar.classList.add('collapsed');
        }
    }

    // Toggle collapse on button click
    collapseBtn.addEventListener('click', () => {
        if (isMobile()) {
            filtersSidebar.classList.toggle('mobile-collapsed');
        } else {
            filtersSidebar.classList.toggle('collapsed');
            localStorage.setItem('projects_filters_collapsed', filtersSidebar.classList.contains('collapsed'));
        }
    });

    // Allow clicking the header area to toggle on mobile
    const header = filtersSidebar.querySelector('.filters-sidebar-header');
    if (header) {
        header.addEventListener('click', (e) => {
            if (isMobile() && !e.target.closest('.filters-collapse-btn')) {
                filtersSidebar.classList.toggle('mobile-collapsed');
            }
        });
    }
}

/**
 * Initialize the collapsible map functionality
 */
function initializeMapCollapse() {
    const mapToggleBtn = document.getElementById('mapToggleBtn');
    const mapWrapper = document.getElementById('mapWrapper');

    if (!mapToggleBtn || !mapWrapper) return;

    // Restore collapsed state from localStorage
    const isCollapsed = localStorage.getItem('projects_map_collapsed') === 'true';
    if (isCollapsed) {
        mapWrapper.classList.add('collapsed');
        mapToggleBtn.querySelector('.map-toggle-icon').classList.replace('fa-chevron-up', 'fa-chevron-down');
    }

    // Toggle collapse on header click
    mapToggleBtn.addEventListener('click', () => {
        mapWrapper.classList.toggle('collapsed');
        const icon = mapToggleBtn.querySelector('.map-toggle-icon');
        if (mapWrapper.classList.contains('collapsed')) {
            icon.classList.replace('fa-chevron-up', 'fa-chevron-down');
        } else {
            icon.classList.replace('fa-chevron-down', 'fa-chevron-up');
        }
        localStorage.setItem('projects_map_collapsed', mapWrapper.classList.contains('collapsed'));
    });
}

/**
 * Initialize navigation quick-add buttons
 * Handles .nav-add elements with data-url attributes
 */
function initializeNavAddButtons() {
    const sidebar = document.querySelector('.sidebar-nav');
    if (!sidebar) return;

    // Event delegation for nav-add buttons
    sidebar.addEventListener('click', (e) => {
        const navAdd = e.target.closest('.nav-add');
        if (navAdd) {
            e.preventDefault();
            e.stopPropagation();
            const url = navAdd.dataset.url;
            if (url) {
                window.location = url;
            }
        }
    });
}


/**
 * Update the badge count for a formset's detail-card header.
 * Counts visible (non-hidden, non-empty-template) data rows.
 */
function updateFormsetBadge(table) {
    const card = table.closest('.detail-card');
    if (!card) return;
    const badge = card.querySelector('.badge-count');
    if (!badge) return;
    const visibleRows = table.querySelectorAll('tbody tr:not(.more):not(.empty):not([style*="display: none"]):not([style*="display:none"])');
    badge.textContent = visibleRows.length;
}

/**
 * Wire up .btn-delete-row buttons to hide the row immediately
 * and check the DELETE checkbox so Django processes the deletion on submit.
 */
function setupDeleteButtons() {
    document.querySelectorAll('table .btn-delete-row').forEach(btn => {
        if (btn._deleteInit) return;
        btn._deleteInit = true;
        btn.addEventListener('click', () => {
            const row = btn.closest('tr');
            if (!row) return;
            const table = row.closest('table');
            const checkbox = row.querySelector('input[type="checkbox"][name$="-DELETE"]');
            if (!checkbox) return;

            checkbox.checked = true;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
            row.style.display = 'none';
            updateFormsetBadge(table);
        });
    });
}

/**
 * Setup autosave for inline formsets (.formset-table).
 * Listens for changes on each table, collects all formset data, and POSTs it.
 */
function setupFormsetAutosave(projectForm, saveUrl) {
    projectForm.querySelectorAll('.formset-table').forEach(table => {
        const prefix = table.dataset.formsetPrefix;
        if (!prefix) return;

        let saveTimer = null;

        table.addEventListener('change', (e) => {
            if (!e.target.matches('input, select, textarea')) return;
            clearTimeout(saveTimer);
            saveTimer = setTimeout(() => {
                const formsetData = {};
                // Collect management form hidden inputs + all row fields
                projectForm.querySelectorAll(
                    `input[name^="${prefix}-"], select[name^="${prefix}-"], textarea[name^="${prefix}-"]`
                ).forEach(f => {
                    if (!f.name) return;
                    if (f.type === 'checkbox') {
                        // Only include checked checkboxes (Django treats absence as False)
                        if (f.checked) formsetData[f.name] = 'on';
                    } else {
                        formsetData[f.name] = f.value;
                    }
                });

                AutoSaveManager.saveCustom(saveUrl, {
                    field_name: prefix,
                    field_value: formsetData,
                }, {
                    indicatorTarget: null,
                    onSuccess: (responseData) => {
                        const status = document.getElementById('autosave-status');
                        if (status) {
                            status.style.display = 'inline-flex';
                            status.classList.remove('saving', 'error');
                            status.querySelector('.autosave-text').textContent = 'All changes saved';
                        }
                        // Sync INITIAL_FORMS and hidden row IDs to prevent
                        // duplicate creation on subsequent autosaves.
                        if (responseData.formset_ids && responseData.initial_forms != null) {
                            const initialEl = document.getElementById(`id_${prefix}-INITIAL_FORMS`);
                            if (initialEl) initialEl.value = responseData.initial_forms;

                            // Update hidden id fields for each row so Django
                            // maps them to existing DB rows on next save.
                            responseData.formset_ids.forEach((dbId, i) => {
                                const idField = document.querySelector(
                                    `input[name="${prefix}-${i}-id"]`
                                );
                                if (idField) idField.value = dbId;
                            });
                        }
                    },
                    onError: () => {
                        const status = document.getElementById('autosave-status');
                        if (status) {
                            status.style.display = 'inline-flex';
                            status.classList.add('error');
                            status.classList.remove('saving');
                            status.querySelector('.autosave-text').textContent = 'Save failed';
                        }
                    },
                    debounceTime: 50,
                });
            }, 500);
        });
    });
}

Dom.callOnceLoaded(() => {
    initUserMenu();
    setupAddableSelect();
    setupNormalSelect();
    initTiptap('.richtext', { maxHeight: 600 });
    setupTextModals();
    setupPrivacyPopup();
    setupConsultantLinks();
    setupDeleteButtons();

    // Initialize tag input for keywords field
    const keywordsField = document.getElementById('id_keywords');
    if (keywordsField) {
        new TagTextInput(keywordsField, { placeholder: 'Add keyword and press Enter' });
    }

    // Image remove button — calls autosave endpoint to clear image
    const btnRemoveImage = document.getElementById('btn-remove-image');
    if (btnRemoveImage) {
        btnRemoveImage.addEventListener('click', async () => {
            const projectForm = document.getElementById('project-form');
            const autosaveUrl = projectForm?.dataset.autosaveUrl;
            if (!autosaveUrl) return;

            try {
                const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value;
                const resp = await fetch(autosaveUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
                    body: JSON.stringify({ field_name: 'image_clear', field_value: true }),
                });
                const result = await resp.json();
                if (result.success) {
                    const thumbnail = document.getElementById('image-preview-link');
                    if (thumbnail) thumbnail.style.display = 'none';
                    btnRemoveImage.style.display = 'none';
                }
            } catch (e) {
                console.error('Failed to remove image:', e);
            }
        });
    }

    // Image upload — sends file via FormData to dedicated endpoint
    const imageInput = document.getElementById('id_image');
    if (imageInput && imageInput.dataset.uploadUrl) {
        imageInput.addEventListener('change', async () => {
            const file = imageInput.files[0];
            if (!file) return;

            const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value;
            const formData = new FormData();
            formData.append('image', file);

            try {
                const resp = await fetch(imageInput.dataset.uploadUrl, {
                    method: 'POST',
                    headers: { 'X-CSRFToken': csrfToken },
                    body: formData,
                });
                const result = await resp.json();
                if (result.success) {
                    // Show/update thumbnail
                    let thumbnail = document.getElementById('image-preview-link');
                    if (!thumbnail) {
                        thumbnail = document.createElement('a');
                        thumbnail.id = 'image-preview-link';
                        thumbnail.className = 'image-thumbnail';
                        thumbnail.target = '_blank';
                        const img = document.createElement('img');
                        img.alt = 'Project image';
                        thumbnail.appendChild(img);
                        const row = imageInput.closest('.image-field-row');
                        if (row) row.prepend(thumbnail);
                    }
                    thumbnail.href = result.url;
                    thumbnail.querySelector('img').src = result.url;
                    thumbnail.style.display = '';
                    // Show remove button if it exists, or create one
                    if (btnRemoveImage) btnRemoveImage.style.display = '';
                }
            } catch (e) {
                console.error('Failed to upload image:', e);
            }
        });
    }

    // initializeFiltersCollapse(); // Handled by DataTable component
    initializeMapCollapse();
    initializeNavAddButtons();

    Array.from(document.getElementsByClassName('form-add')).forEach(elem => {
        elem.onclick = function() {
            const emptyFormId = this.getAttribute('empty-nb');
            const nbFormId = this.getAttribute('field-name') + '-TOTAL_FORMS';
            addForm(nbFormId, emptyFormId, [setupTextModals, setupConsultantLinks, setupNormalSelect, setupDeleteButtons]);
            // Update badge count after adding a row
            const table = this.closest('table');
            if (table) updateFormsetBadge(table);
        };
    });
    let table = null;
    const dataElem = document.getElementById('page-data');
    if (dataElem) {
        table = initializeProjectsTable();
    }

    // Initialize map if on projects page (after table initialization)
    if (document.getElementById('projects-map') && table && table.dataTable) {
        const mapResult = initializeProjectsMap('projects-map');

        if (mapResult) {
            // Function to update map from current table state
            const updateMap = () => {
                const filteredData = table.dataTable.getFilteredData();
                if (filteredData && filteredData.length > 0) {
                    updateMapFromTable(filteredData);
                }
            };

            // Wait for map data to load, then update with initial table data
            mapResult.ready.then(() => {
                updateMap();

                // Listen for table changes to update map
                const tableContainer = document.getElementById('projectsTable');
                if (tableContainer) {
                    // Listen to custom selectionChange events
                    tableContainer.addEventListener('selectionChange', updateMap);

                    // Observe tbody changes for filter updates
                    const tbody = tableContainer.querySelector('tbody');
                    if (tbody) {
                        const observer = new MutationObserver(updateMap);
                        observer.observe(tbody, { childList: true });
                    }
                }
            }).catch(error => {
                console.error('Failed to initialize map:', error);
            });
        }
    }
    setupExport(table);
    setupProjectTemplateManagement(table);
    document.querySelectorAll('select[multiple]').forEach( select => {
        // Skip location-search, addable selects, and filter-multiselect (managed by DataTable)
        if (select.name.includes('location-search') ||
            select.classList.contains('addable') ||
            select.classList.contains('filter-multiselect')) return;
        setupSlimSelect(select);
    });
    Array.from(document.querySelectorAll('.monthpicker')).forEach(e => {
        new Datepicker(e, {pickLevel: 1, format:'dd/mm/yyyy'});
    });
    // Setup documents filesystem if exists
    const docsContainer = document.getElementById('documents-mount');
    if (docsContainer) {
        new Filesystem({
            target: docsContainer,
        });
    }

    // Setup autosave for project detail form
    const projectForm = document.getElementById('project-form');
    if (projectForm && projectForm.dataset.autosaveUrl) {
        setupFormsetAutosave(projectForm, projectForm.dataset.autosaveUrl);
        AutoSaveManager.setupAutoSave(projectForm, {
            saveUrl: projectForm.dataset.autosaveUrl,
            excludeFields: [
                'csrfmiddlewaretoken',
                // File/image fields (can't autosave via JSON)
                'image',
                // Formset management fields
                'projectemployee_set-TOTAL_FORMS', 'projectemployee_set-INITIAL_FORMS',
                'projectemployee_set-MIN_NUM_FORMS', 'projectemployee_set-MAX_NUM_FORMS',
                'Project_codes-TOTAL_FORMS', 'Project_codes-INITIAL_FORMS',
                'Project_codes-MIN_NUM_FORMS', 'Project_codes-MAX_NUM_FORMS',
                'projectconsultant_set-TOTAL_FORMS', 'projectconsultant_set-INITIAL_FORMS',
                'projectconsultant_set-MIN_NUM_FORMS', 'projectconsultant_set-MAX_NUM_FORMS',
                'projectpartner_set-TOTAL_FORMS', 'projectpartner_set-INITIAL_FORMS',
                'projectpartner_set-MIN_NUM_FORMS', 'projectpartner_set-MAX_NUM_FORMS',
            ],
            excludeSelector: '[data-no-autosave], .formset-table input, .formset-table select, .formset-table textarea',
            onSave: () => {
                const status = document.getElementById('autosave-status');
                if (status) {
                    status.style.display = 'inline-flex';
                    status.classList.remove('saving', 'error');
                    status.querySelector('.autosave-text').textContent = 'All changes saved';
                }
            },
            onError: () => {
                const status = document.getElementById('autosave-status');
                if (status) {
                    status.style.display = 'inline-flex';
                    status.classList.add('error');
                    status.classList.remove('saving');
                    status.querySelector('.autosave-text').textContent = 'Save failed';
                }
            },
        });
    }

    // Setup archive analysis if on project detail page with archive
    const archiveAnalysis = document.getElementById('archive-analysis');
    if (archiveAnalysis) {
        initArchiveAnalysis(parseInt(archiveAnalysis.dataset.projectId));
    }

    // "Likelihood to recommend Altai" star rating on the contact page
    document.querySelectorAll('[data-contact-recommend]').forEach(initContactRecommendRating);

    // Field guideline info boxes (hover popovers next to form labels)
    initFieldGuidelines();

    // Contact create/edit modal on the project form (slim-select must be
    // initialized first — see setupAddableSelect above)
    installContactModal();

    // Render data quality badges
    document.querySelectorAll('.data-quality-badge').forEach(badge => {
        const score = parseFloat(badge.dataset.score);
        const missing = badge.dataset.missing;
        let dots = '';
        for (let i = 1; i <= 5; i++) {
            if (score >= i) {
                dots += '<i class="fas fa-circle"></i>';
            } else if (score >= i - 0.5) {
                dots += '<i class="fas fa-circle-half-stroke"></i>';
            } else {
                dots += '<i class="far fa-circle"></i>';
            }
        }
        const label = score + '/5';
        badge.innerHTML = `<span class="dq-dots">${dots}</span><span class="dq-label">${label}</span>`;

        // Tooltip on hover
        if (missing) {
            badge.title = 'Missing: ' + missing;
        } else {
            badge.title = 'All fields complete';
        }
        if (window.bootstrap?.Tooltip) {
            new bootstrap.Tooltip(badge, { placement: 'right' });
        }
    });

    // Inline "New partner" modal for the People tab.
    // After creating, append the option to the partner select on every row.
    // The user picks the new partner on the row they want, autosave does the rest.
    installPartnerPicker();
});